import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Save,
  X,
  Plus,
  Calendar,
  DollarSign,
  Users,
  Target,
  AlertTriangle,
  FileText,
  Settings,
  Clock,
  BarChart3,
  CheckCircle,
  Upload,
  Link,
  Tag,
  MapPin,
  Phone,
  Mail,
  Globe,
  Building,
  User,
  Briefcase,
  PieChart,
  TrendingUp,
  Shield,
  Zap
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import MasterDataService from '../../services/masterDataService';

const EditProject = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  // Tab management
  const [activeTab, setActiveTab] = useState('basic');
  
  // Project categories from master data
  const [projectCategories, setProjectCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const { success, error: showError, toasts, removeToast } = useToast();
  // Form state
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    description: '',
    category: '',
    priority: '',
    status: '',
    projectCode: '',
    department: '',
    projectType: '',
    businessUnit: '',
    
    // Timeline & Budget
    startDate: '',
    endDate: '',
    estimatedDuration: '',
    budget: '',
    currency: 'USD',
    costCenter: '',
    
    // Finance & Budgeting
    budgetBreakdown: {
      labor: 0,
      materials: 0,
      equipment: 0,
      travel: 0,
      other: 0
    },
    expenseCategories: [],
    financialMilestones: [],
    budgetApprovalRequired: false,
    budgetApprover: '',
    costAllocation: '',
    roiTarget: '',
    financialReporting: '',
    budgetVarianceThreshold: 0,
    expenseApprovalLimit: 0,
    
    // Billing Milestones
    billingMilestones: [],
    
    // Team & Resources
    projectManager: '',
    stakeholders: [],
    externalVendors: [],
    resources: [],
    stakeholderDetails: [],
    
    // Project Details
    objectives: [],
    deliverables: [],
    milestones: [],
    risks: [],
    assumptions: [],
    constraints: [],
    
    // Communication & Reporting
    reportingFrequency: '',
    communicationChannels: [],
    meetingSchedule: '',
    reportingTemplate: '',
    
    // Integration & Tools
    projectTools: [],
    integrations: [],
    documentRepository: '',
    knowledgeBase: '',
    
    // Compliance & Security
    complianceRequirements: [],
    securityLevel: '',
    dataClassification: '',
    approvalWorkflow: '',
    
    // Advanced Settings
    autoNotifications: false,
    timeTracking: false,
    budgetAlerts: false,
    riskMonitoring: false,
    customFields: []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // Editable project team (Project.team) – this is the core project team list.
  // Each entry is { userId, name, email, role, joinedDate } for UI purposes.
  const [projectTeam, setProjectTeam] = useState([]);
  // Team Collaboration members for picker dropdown
  const [teamMemberOptions, setTeamMemberOptions] = useState([]);
  const [selectedTeamMemberId, setSelectedTeamMemberId] = useState('');
  const [teamRoleInput, setTeamRoleInput] = useState('');
  // Team Collaboration link: team members that have this project in their assignedProjects.
  // This mirrors the ViewProject behaviour so that the "Team & Planning" tab also shows
  // which Team Collaboration members are linked to this project.
  const [assignedTeamMembers, setAssignedTeamMembers] = useState([]);

  // Load project data on component mount
  useEffect(() => {
    const loadProject = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_CONFIG.ENDPOINTS.PROJECTS}/${id}`, {
          headers: API_CONFIG.HEADERS
        });
        
        if (response.ok) {
          const responseData = await response.json();
          console.log('API Response:', responseData);
          
          // Handle both old format { project, tasks } and new format { success, data: { project, tasks } }
          const project = responseData.data?.project || responseData.project || responseData;
          console.log('Project data:', project);
          console.log('Project name:', project.name);
          console.log('Project description:', project.description);
          console.log('Project category:', project.category);
          console.log('Project priority:', project.priority);
          console.log('Project status:', project.status);
          
          // Populate form data with project data
          setFormData({
            // Basic Information
            name: project.name || '',
            description: project.description || '',
            category: project.category || '',
            priority: project.priority || '',
            status: project.status || '',
            projectCode: project.projectCode || '',
            department: project.department || '',
            projectType: project.projectType || '',
            businessUnit: project.businessUnit || '',
            
            // Timeline & Budget
            startDate: project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : '',
            endDate: project.endDate ? new Date(project.endDate).toISOString().split('T')[0] : '',
            estimatedDuration: project.estimatedDuration || '',
            budget: project.budget || '',
            currency: project.currency || 'USD',
            costCenter: project.costCenter || '',
            
            // Finance & Budgeting
            budgetBreakdown: project.budgetBreakdown || {
              labor: 0,
              materials: 0,
              equipment: 0,
              travel: 0,
              other: 0
            },
            expenseCategories: project.expenseCategories || [],
            financialMilestones: project.financialMilestones || [],
            budgetApprovalRequired: project.budgetApprovalRequired || false,
            budgetApprover: project.budgetApprover || '',
            costAllocation: project.costAllocation || '',
            roiTarget: project.roiTarget || '',
            financialReporting: project.financialReporting || '',
            budgetVarianceThreshold: project.budgetVarianceThreshold || 0,
            expenseApprovalLimit: project.expenseApprovalLimit || 0,
            
            // Billing Milestones
            billingMilestones: project.billingMilestones ? project.billingMilestones.map(milestone => ({
              ...milestone,
              billingDate: milestone.billingDate ? new Date(milestone.billingDate).toISOString().split('T')[0] : ''
            })) : [],
            
            // Team & Resources
            // Handle projectManager - prioritize projectManagerName, then populated user, then ID
            projectManager: project.projectManagerName 
              || (project.projectManager && typeof project.projectManager === 'object' 
                  ? (project.projectManager.name || project.projectManager.fullName || project.projectManager.email || '')
                  : project.projectManager)
              || '',
            stakeholders: project.stakeholders || [],
            externalVendors: project.externalVendors || [],
            resources: project.resources || [],
            stakeholderDetails: project.stakeholderDetails || [],
            
            // Project Details
            objectives: project.objectives || [],
            deliverables: project.deliverables || [],
            milestones: project.milestones ? project.milestones.map(milestone => ({
              name: milestone.name || '',
              description: milestone.description || '',
              dueDate: milestone.dueDate ? new Date(milestone.dueDate).toISOString().split('T')[0] : '',
              status: milestone.status || 'Not Started',
              deliverables: milestone.deliverables || []
            })) : [],
            risks: project.risks || [],
            assumptions: project.assumptions || [],
            constraints: project.constraints || [],
            
            // Communication & Reporting
            reportingFrequency: project.reportingFrequency || '',
            communicationChannels: project.communicationChannels || [],
            meetingSchedule: project.meetingSchedule || '',
            reportingTemplate: project.reportingTemplate || '',
            
            // Integration & Tools
            projectTools: project.projectTools || [],
            integrations: project.integrations || [],
            documentRepository: project.documentRepository || '',
            knowledgeBase: project.knowledgeBase || '',
            
            // Compliance & Security
            complianceRequirements: project.complianceRequirements || [],
            securityLevel: project.securityLevel || '',
            dataClassification: project.dataClassification || '',
            approvalWorkflow: project.approvalWorkflow || '',
            
            // Advanced Settings
            autoNotifications: project.autoNotifications || false,
            timeTracking: project.timeTracking || false,
            budgetAlerts: project.budgetAlerts || false,
            riskMonitoring: project.riskMonitoring || false,
            customFields: project.customFields || []
          });

          // Populate editable project team from Project.team (if present)
          const rawTeam = Array.isArray(project.team) ? project.team : [];
          const mappedTeam = rawTeam
            .filter(Boolean)
            .map((member) => {
              const user = member.user || member.userId || member.id;
              const hasUserObject =
                user && typeof user === 'object' && (user._id || user.id);
              const userId = hasUserObject
                ? user._id || user.id
                : typeof user === 'string'
                ? user
                : null;

              // Safely derive display name/email without assuming member.user is non-null
              let displayName = 'Team Member';
              let email = '';
              if (member.user && typeof member.user === 'object') {
                displayName =
                  member.user.name ||
                  member.user.fullName ||
                  member.user.email ||
                  'Team Member';
                email = member.user.email || '';
              } else if (typeof member.user === 'string') {
                displayName = member.user;
              }

              return {
                userId,
                name: displayName,
                email,
                role: member.role || 'Team Member',
                joinedDate: member.joinedDate || member.createdAt || null
              };
            })
            // Filter out entries without a valid userId (legacy/seeded nulls)
            .filter((m) => m.userId);
          setProjectTeam(mappedTeam);
        } else {
          const errorData = await response.json();
          console.error('Failed to load project:', response.status, errorData);
          showError(`Failed to load project: ${errorData.message || 'Unknown error'}`);
          navigate('/projects/project-management');
        }
      } catch (error) {
        console.error('Error loading project:', error);
        showError(`Error loading project data: ${error.message}`);        navigate('/projects/project-management');
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      console.log('Loading project with ID:', id);
      console.log('API Endpoint:', `${API_CONFIG.ENDPOINTS.PROJECTS}/${id}`);
      loadProject();
    } else {
      console.error('No project ID provided');
      showError('No project ID provided');
      navigate('/projects/project-management');
    }
  }, [id, navigate]);

  // Load team members that have this project assigned in Team Collaboration.
  // This does NOT change project data; it is a read-only view that keeps Projects
  // and Team Collaboration visually in sync.
  useEffect(() => {
    const loadAssignedTeamMembers = async () => {
      if (!id) return;
      try {
        const response = await fetch(`${API_CONFIG.ENDPOINTS.TEAM_MEMBERS}?limit=500`, {
          headers: API_CONFIG.HEADERS
        });
        if (!response.ok) return;
        const json = await response.json();
        const members = json.data || json || [];
        const filtered = members.filter((member) =>
          (member.assignedProjects || []).some((p) => {
            if (!p || !p.project) return false;
            return String(p.project) === String(id);
          })
        );
        setAssignedTeamMembers(filtered);
      } catch (err) {
        console.error('Error loading assigned team members for project:', err);
      }
    };

    loadAssignedTeamMembers();
  }, [id]);

  // Load team member options for the project team picker (from Team Collaboration dropdown API)
  useEffect(() => {
    const loadTeamMemberOptions = async () => {
      try {
        const response = await fetch(API_CONFIG.ENDPOINTS.TEAM_MEMBERS_DROPDOWN, {
          headers: API_CONFIG.HEADERS
        });
        if (!response.ok) return;
        const json = await response.json();
        const items = json.data || json || [];
        setTeamMemberOptions(items);
      } catch (err) {
        console.error('Error loading team member dropdown options:', err);
      }
    };

    loadTeamMemberOptions();
  }, []);

  // Load project categories on component mount
  useEffect(() => {
    const loadProjectCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await MasterDataService.getByCategory('PROJECTCATEGORY');
        if (response.success && response.data) {
          // Filter only active categories and map to dropdown format
          const categories = response.data
            .filter(item => item.isActive)
            .map(item => ({
              code: item.code,
              label: item.label,
              description: item.description
            }));
          setProjectCategories(categories);
        }
      } catch (error) {
        console.error('Error loading project categories:', error);
        // Fallback to empty array if API fails
        setProjectCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };
    
    loadProjectCategories();
  }, []);

  // Tab configuration - Enhanced with Finance
  const tabs = [
    {
      id: 'basic',
      label: 'Project Setup',
      icon: <FileText size={18} />
    },
    {
      id: 'team',
      label: 'Team & Planning',
      icon: <Users size={18} />
    },
    {
      id: 'finance',
      label: 'Finance & Budgeting',
      icon: <DollarSign size={18} />
    },
    {
      id: 'communication',
      label: 'Communication & Tools',
      icon: <Globe size={18} />
    },
    {
      id: 'advanced',
      label: 'Advanced Settings',
      icon: <Settings size={18} />
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayAdd = (field, value) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: field === 'milestones' 
          ? [...prev[field], { name: value.trim(), description: '', dueDate: '', status: 'Not Started', deliverables: [] }]
          : [...prev[field], value.trim()]
      }));
    }
  };

  const handleArrayRemove = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  // ----- Project Team handlers (Project.team) -----
  const handleAddProjectTeamMember = () => {
    if (!selectedTeamMemberId) return;
    const alreadyExists = projectTeam.some(
      (m) => String(m.userId) === String(selectedTeamMemberId)
    );
    if (alreadyExists) return;

    const member = teamMemberOptions.find(
      (opt) => String(opt.id) === String(selectedTeamMemberId)
    );
    if (!member) return;

    setProjectTeam((prev) => [
      ...prev,
      {
        userId: member.id,
        name: member.label || member.name || member.email || 'Team Member',
        email: member.email || '',
        role: teamRoleInput || 'Team Member',
        joinedDate: new Date().toISOString()
      }
    ]);
    // reset inputs
    setSelectedTeamMemberId('');
    setTeamRoleInput('');
  };

  const handleUpdateProjectTeamRole = (userId, role) => {
    setProjectTeam((prev) =>
      prev.map((m) =>
        String(m.userId) === String(userId) ? { ...m, role } : m
      )
    );
  };

  const handleRemoveProjectTeamMember = (userId) => {
    setProjectTeam((prev) =>
      prev.filter((m) => String(m.userId) !== String(userId))
    );
  };

  const handleBillingMilestoneChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      billingMilestones: prev.billingMilestones.map((milestone, i) => 
        i === index ? { ...milestone, [field]: value } : milestone
      )
    }));
  };

  const handleAddBillingMilestone = () => {
    const newMilestone = {
      name: '',
      billingDate: '',
      amount: '',
      currency: 'USD',
      trigger: 'date',
      description: '',
      autoGenerate: false
    };
    
    setFormData(prev => ({
      ...prev,
      billingMilestones: [...prev.billingMilestones, newMilestone]
    }));
  };

  const handleRemoveBillingMilestone = (index) => {
    setFormData(prev => ({
      ...prev,
      billingMilestones: prev.billingMilestones.filter((_, i) => i !== index)
    }));
  };

  const handleResourceChange = (index, field, value) => {
    setFormData(prev => {
      // Safety check: ensure resources is an array
      if (!Array.isArray(prev.resources)) {
        console.error('CRITICAL: Resources is not an array in handleResourceChange:', typeof prev.resources);
        return prev;
      }
      
      return {
      ...prev,
      resources: prev.resources.map((resource, i) => 
        i === index ? { ...resource, [field]: value } : resource
      )
      };
    });
  };

  const handleAddResource = () => {
    const newResource = {
      name: '',
      type: '',
      role: '',
      rate: '',
      currency: 'USD',
      hours: '',
      startDate: '',
      endDate: '',
      status: 'active',
      skills: [],
      experience: '',
      location: '',
      contact: '',
      notes: ''
    };
    
    setFormData(prev => {
      // Safety check: ensure resources is an array
      if (!Array.isArray(prev.resources)) {
        console.error('CRITICAL: Resources is not an array in handleAddResource:', typeof prev.resources);
        return {
          ...prev,
          resources: [newResource]
        };
      }
      
      return {
      ...prev,
      resources: [...prev.resources, newResource]
      };
    });
  };

  const handleRemoveResource = (index) => {
    setFormData(prev => ({
      ...prev,
      resources: prev.resources.filter((_, i) => i !== index)
    }));
  };

  const handleStakeholderChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      stakeholderDetails: prev.stakeholderDetails.map((stakeholder, i) => 
        i === index ? { ...stakeholder, [field]: value } : stakeholder
      )
    }));
  };

  const handleAddStakeholder = () => {
    const newStakeholder = {
      name: '',
      role: '',
      organization: '',
      email: '',
      phone: '',
      influence: 'medium',
      interest: 'medium',
      availability: 'available',
      communicationPreference: 'email',
      involvement: 'regular',
      notes: ''
    };
    
    setFormData(prev => ({
      ...prev,
      stakeholderDetails: [...prev.stakeholderDetails, newStakeholder]
    }));
  };

  const handleRemoveStakeholder = (index) => {
    setFormData(prev => ({
      ...prev,
      stakeholderDetails: prev.stakeholderDetails.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const toTitleCase = (str) => {
        if (!str) return '';
        // Special handling for "On Hold" to keep the space
        if (str.toUpperCase() === 'ON HOLD') return 'On Hold';
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
      };
      // Format data for backend with proper data types
      const projectData = {
        ...formData,
        status: toTitleCase(formData.status),
        priority: toTitleCase(formData.priority),
        // Convert string dates to Date objects
        startDate: formData.startDate ? new Date(formData.startDate) : null,
        endDate: formData.endDate ? new Date(formData.endDate) : null,
        // Convert budget to number
        budget: formData.budget ? parseFloat(formData.budget) : 0,
        // Convert budget breakdown to numbers
        budgetBreakdown: {
          labor: parseFloat(formData.budgetBreakdown?.labor) || 0,
          materials: parseFloat(formData.budgetBreakdown?.materials) || 0,
          equipment: parseFloat(formData.budgetBreakdown?.equipment) || 0,
          travel: parseFloat(formData.budgetBreakdown?.travel) || 0,
          other: parseFloat(formData.budgetBreakdown?.other) || 0
        },
        // Convert billing milestones with proper data types
        billingMilestones: (formData.billingMilestones || []).map(milestone => ({
          ...milestone,
          billingDate: milestone.billingDate ? new Date(milestone.billingDate) : null,
          amount: parseFloat(milestone.amount) || 0
        })),
        // Convert resources with proper data types - simplified approach
        resources: Array.isArray(formData.resources) 
          ? formData.resources.map(resource => ({
              name: String(resource.name || ''),
              type: String(resource.type || ''),
              role: String(resource.role || ''),
              rate: parseFloat(resource.rate) || 0,
              currency: String(resource.currency || 'USD'),
              hours: parseFloat(resource.hours) || 0,
              startDate: resource.startDate ? new Date(resource.startDate) : null,
              endDate: resource.endDate ? new Date(resource.endDate) : null,
              status: String(resource.status || 'active'),
              skills: Array.isArray(resource.skills) ? resource.skills.map(s => String(s)) : [],
              experience: String(resource.experience || ''),
              location: String(resource.location || ''),
              contact: String(resource.contact || ''),
              notes: String(resource.notes || '')
            }))
          : [],
        // Convert milestones with proper data types
        milestones: (formData.milestones || []).map(milestone => {
          if (typeof milestone === 'string') {
            return {
              name: milestone,
              description: '',
              dueDate: null,
              status: 'Not Started',
              deliverables: []
            };
          }
          return {
            ...milestone,
            dueDate: milestone.dueDate ? new Date(milestone.dueDate) : null
          };
        }),
        // Convert financial milestones with proper data types
        financialMilestones: (formData.financialMilestones || []).map(milestone => ({
          ...milestone,
          amount: parseFloat(milestone.amount) || 0,
          date: milestone.date ? new Date(milestone.date) : null
        })),
        // Convert numeric fields
        roiTarget: parseFloat(formData.roiTarget) || 0,
        budgetVarianceThreshold: parseFloat(formData.budgetVarianceThreshold) || 0,
        expenseApprovalLimit: parseFloat(formData.expenseApprovalLimit) || 0,
        // Ensure arrays are properly formatted
        objectives: Array.isArray(formData.objectives) ? formData.objectives : [],
        deliverables: Array.isArray(formData.deliverables) ? formData.deliverables : [],
        risks: Array.isArray(formData.risks) ? formData.risks : [],
        assumptions: Array.isArray(formData.assumptions) ? formData.assumptions : [],
        constraints: Array.isArray(formData.constraints) ? formData.constraints : [],
        stakeholders: Array.isArray(formData.stakeholders) ? formData.stakeholders : [],
        externalVendors: Array.isArray(formData.externalVendors) ? formData.externalVendors : [],
        stakeholderDetails: Array.isArray(formData.stakeholderDetails) ? formData.stakeholderDetails : [],
        communicationChannels: Array.isArray(formData.communicationChannels) ? formData.communicationChannels : [],
        projectTools: Array.isArray(formData.projectTools) ? formData.projectTools : [],
        integrations: Array.isArray(formData.integrations) ? formData.integrations : [],
        complianceRequirements: Array.isArray(formData.complianceRequirements) ? formData.complianceRequirements : [],
        customFields: Array.isArray(formData.customFields) ? formData.customFields : [],
        expenseCategories: Array.isArray(formData.expenseCategories) ? formData.expenseCategories : [],
        // Project team: map UI model back to backend Project.team shape
        team: projectTeam.map((member) => ({
          user: member.userId,
          role: member.role || 'Team Member',
          joinedDate: member.joinedDate ? new Date(member.joinedDate) : new Date()
        }))
      };

      const response = await fetch(`${API_CONFIG.ENDPOINTS.PROJECTS}/${id}`, {
        method: 'PUT',
        headers: API_CONFIG.HEADERS,
        body: JSON.stringify(projectData)
      });
      
      if (response.ok) {
        const responseData = await response.json();
        // Handle both old format (project object) and new format { success, data: { project } }
        const project = responseData.data?.project || responseData;
        success('Project updated successfully!');
        navigate('/projects/project-management');
      } else {
        const errorData = await response.json();
        console.error('Backend error:', errorData);
        
        // Show user-friendly error message
        let userMessage = 'Failed to update project. Please check your data and try again.';
        
        if (errorData.message) {
          if (errorData.message.includes('validation failed')) {
            userMessage = 'Please check all required fields and ensure data is properly formatted.';
          } else if (errorData.message.includes('Cast to')) {
            userMessage = 'There was an issue with the data format. Please try again.';
          } else {
            userMessage = errorData.message;
          }
        }
        showError(userMessage);
      }
    } catch (err) {
      console.error('Error updating project:', err);
      showError('Failed to update project. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderBasicInformation = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Basic Information</h3>
        <p className="section-description">Project name, description, and basic details</p>
      </div>
      
      {/* 4-Column Grid Layout */}
      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">Project Name *</label>
          <input
            type="text"
            className="form-control"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Enter project name"
            required
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Category</label>
          <select
            className="form-select"
            value={formData.category}
            onChange={(e) => handleInputChange('category', e.target.value)}
            disabled={loadingCategories}
          >
            <option value="">Select category</option>
            {projectCategories.length > 0 ? (
              projectCategories.map((category) => (
                <option key={category.code} value={category.code}>
                  {category.label}
                </option>
              ))
            ) : (
              !loadingCategories && (
                <>
                  <option value="software">Software Development</option>
                  <option value="infrastructure">Infrastructure</option>
                  <option value="marketing">Marketing</option>
                  <option value="research">Research & Development</option>
                  <option value="operations">Operations</option>
                  <option value="consulting">Consulting</option>
                </>
              )
            )}
          </select>
          {loadingCategories && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              Loading categories...
            </small>
          )}
        </div>
        
        <div className="form-group">
          <label className="form-label">Priority</label>
          <select
            className="form-select"
            value={formData.priority}
            onChange={(e) => handleInputChange('priority', e.target.value)}
          >
            <option value="Low">Low</option>  
            <option value="Medium">Medium</option> 
            <option value="High">High</option>    
            <option value="Critical">Critical</option> 
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Status</label>
          <select
            className="form-select"
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
          >
            <option value="Planning">Planning</option>
            <option value="Active">Active</option> 
            <option value="On Hold">On Hold</option>  
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Project Code</label>
          <input
            type="text"
            className="form-control"
            value={formData.projectCode || ''}
            onChange={(e) => handleInputChange('projectCode', e.target.value)}
            placeholder="e.g., PRJ-2024-001"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Department</label>
          <select
            className="form-select"
            value={formData.department || ''}
            onChange={(e) => handleInputChange('department', e.target.value)}
          >
            <option value="">Select department</option>
            <option value="development">Development</option>
            <option value="engineering">Engineering</option>
            <option value="marketing">Marketing</option>
            <option value="sales">Sales</option>
            <option value="hr">Human Resources</option>
            <option value="finance">Finance</option>
            <option value="operations">Operations</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Project Type</label>
          <select
            className="form-select"
            value={formData.projectType || ''}
            onChange={(e) => handleInputChange('projectType', e.target.value)}
          >
            <option value="">Select type</option>
            <option value="internal">Internal</option>
            <option value="client">Client Project</option>
            <option value="research">Research</option>
            <option value="maintenance">Maintenance</option>
            <option value="upgrade">Upgrade</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Business Unit</label>
          <input
            type="text"
            className="form-control"
            value={formData.businessUnit || ''}
            onChange={(e) => handleInputChange('businessUnit', e.target.value)}
            placeholder="e.g., North America, EMEA"
          />
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Project Description</label>
        <textarea
          className="form-control"
          rows="4"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Describe the project objectives, scope, and key deliverables..."
        />
      </div>
    </div>
  );

  const renderTimelineBudget = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Timeline & Budget</h3>
        <p className="section-description">Schedule, duration, and financial planning</p>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Start Date *</label>
          <input
            type="date"
            className="form-control"
            value={formData.startDate}
            onChange={(e) => handleInputChange('startDate', e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label className="form-label">End Date</label>
          <input
            type="date"
            className="form-control"
            value={formData.endDate}
            onChange={(e) => handleInputChange('endDate', e.target.value)}
          />
        </div>
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Estimated Duration</label>
          <input
            type="text"
            className="form-control"
            value={formData.estimatedDuration}
            onChange={(e) => handleInputChange('estimatedDuration', e.target.value)}
            placeholder="e.g., 3 months, 6 weeks"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Budget</label>
          <div className="budget-input-group">
            <input
              type="number"
              className="form-control budget-amount"
              value={formData.budget}
              onChange={(e) => handleInputChange('budget', e.target.value)}
              placeholder="0"
            />
            <select
              className="form-select budget-currency"
              value={formData.currency}
              onChange={(e) => handleInputChange('currency', e.target.value)}
            >
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="INR">INR</option>
            </select>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Cost Center</label>
        <input
          type="text"
          className="form-control"
          value={formData.costCenter}
          onChange={(e) => handleInputChange('costCenter', e.target.value)}
          placeholder="Enter cost center code"
        />
      </div>
    </div>
  );

  const renderTeamResources = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Team & Resources</h3>
        <p className="section-description">Team members, stakeholders, and resources</p>
      </div>
      <div className="form-group">
        <label className="form-label">Project Manager *</label>
        <input
          type="text"
          className="form-control"
          value={formData.projectManager || ''}
          onChange={(e) => handleInputChange('projectManager', e.target.value)}
          placeholder="Enter project manager name"
          required
        />
      </div>

      {/* Editable Project Team (Project.team) */}
      <div className="section-header">
        <h3 className="section-title">Project Team</h3>
        <p className="section-description">
          Core team members stored on the project (Project.team). This is separate from
          the Team Collaboration assignments but often overlaps.
        </p>
      </div>
      <div className="form-group mb-3">
        <label className="form-label">Add Team Member</label>
        <div className="form-grid-3">
          <div className="form-group">
            <select
              className="form-select"
              value={selectedTeamMemberId}
              onChange={(e) => setSelectedTeamMemberId(e.target.value)}
            >
              <option value="">Select a team member</option>
              {teamMemberOptions
                .filter(
                  (opt) =>
                    !projectTeam.some(
                      (m) => String(m.userId) === String(opt.id)
                    )
                )
                .map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label || opt.name || opt.email}{' '}
                    {opt.email ? `(${opt.email})` : ''}
                  </option>
                ))}
            </select>
          </div>
          <div className="form-group">
            <input
              type="text"
              className="form-control"
              placeholder="Role (e.g., Developer, BA)"
              value={teamRoleInput}
              onChange={(e) => setTeamRoleInput(e.target.value)}
            />
          </div>
          <div className="form-group d-flex align-items-center">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleAddProjectTeamMember}
              disabled={!selectedTeamMemberId}
            >
              <Plus size={16} className="me-1" />
              Add to Team
            </button>
          </div>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Current Project Team</label>
        <div className="array-items">
          {projectTeam.length > 0 ? (
            projectTeam.map((member) => (
              <div key={member.userId} className="array-item d-flex justify-content-between align-items-center">
                <div>
                  <div className="fw-semibold">
                    {member.name || member.email || 'Team Member'}
                  </div>
                  {member.email && (
                    <div className="text-muted small">{member.email}</div>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="text"
                    className="form-control"
                    style={{ maxWidth: '180px' }}
                    value={member.role || ''}
                    onChange={(e) =>
                      handleUpdateProjectTeamRole(member.userId, e.target.value)
                    }
                    placeholder="Role"
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleRemoveProjectTeamMember(member.userId)}
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <span className="text-muted">
              No team members added to the project team yet.
            </span>
          )}
        </div>
      </div>

      {/* Read-only view of Team Collaboration assignments so Projects and Team Collaboration stay in sync */}
      <div className="form-group">
        <label className="form-label">Assigned Team Members (via Team Collaboration)</label>
        <div className="array-items">
          {assignedTeamMembers.length > 0 ? (
            assignedTeamMembers.map((member) => (
              <div key={member._id || member.id} className="array-item read-only-item">
                <span>
                  {member.name || member.email || 'Team Member'}
                  {member.department ? ` • ${member.department}` : ''}
                </span>
              </div>
            ))
          ) : (
            <span className="text-muted">
              No team members have this project assigned from the Team Collaboration module.
            </span>
          )}
        </div>
      </div>
      
      <div className="section-header">
        <h3 className="section-title">Stakeholder Management</h3>
        <p className="section-description">Detailed stakeholder information with influence, interest, and communication preferences</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Project Stakeholders</label>
        <div className="stakeholders-container">
          {formData.stakeholderDetails.map((stakeholder, index) => (
            <div key={index} className="stakeholder-item">
              <div className="form-grid-4">
                <div className="form-group">
                  <label className="form-label">Stakeholder Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={stakeholder.name}
                    onChange={(e) => handleStakeholderChange(index, 'name', e.target.value)}
                    placeholder="e.g., John Smith"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <input
                    type="text"
                    className="form-control"
                    value={stakeholder.role}
                    onChange={(e) => handleStakeholderChange(index, 'role', e.target.value)}
                    placeholder="e.g., Client Sponsor"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Organization</label>
                  <input
                    type="text"
                    className="form-control"
                    value={stakeholder.organization}
                    onChange={(e) => handleStakeholderChange(index, 'organization', e.target.value)}
                    placeholder="e.g., ABC Corp"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Influence Level</label>
                  <select
                    className="form-select"
                    value={stakeholder.influence}
                    onChange={(e) => handleStakeholderChange(index, 'influence', e.target.value)}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>
              
              <div className="form-grid-4">
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={stakeholder.email}
                    onChange={(e) => handleStakeholderChange(index, 'email', e.target.value)}
                    placeholder="email@example.com"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    className="form-control"
                    value={stakeholder.phone}
                    onChange={(e) => handleStakeholderChange(index, 'phone', e.target.value)}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Interest Level</label>
                  <select
                    className="form-select"
                    value={stakeholder.interest}
                    onChange={(e) => handleStakeholderChange(index, 'interest', e.target.value)}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Availability</label>
                  <select
                    className="form-select"
                    value={stakeholder.availability}
                    onChange={(e) => handleStakeholderChange(index, 'availability', e.target.value)}
                  >
                    <option value="available">Available</option>
                    <option value="limited">Limited</option>
                    <option value="unavailable">Unavailable</option>
                    <option value="unknown">Unknown</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Communication Preference</label>
                  <select
                    className="form-select"
                    value={stakeholder.communicationPreference}
                    onChange={(e) => handleStakeholderChange(index, 'communicationPreference', e.target.value)}
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="meeting">In-Person Meeting</option>
                    <option value="video">Video Call</option>
                    <option value="any">Any Method</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Involvement Level</label>
                  <select
                    className="form-select"
                    value={stakeholder.involvement}
                    onChange={(e) => handleStakeholderChange(index, 'involvement', e.target.value)}
                  >
                    <option value="minimal">Minimal</option>
                    <option value="regular">Regular</option>
                    <option value="frequent">Frequent</option>
                    <option value="constant">Constant</option>
                  </select>
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Notes</label>
                <input
                  type="text"
                  className="form-control"
                  value={stakeholder.notes}
                  onChange={(e) => handleStakeholderChange(index, 'notes', e.target.value)}
                  placeholder="Additional stakeholder information"
                />
              </div>
              
              <div className="stakeholder-actions">
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleRemoveStakeholder(index)}
                >
                  <X size={16} />
                  Remove Stakeholder
                </button>
              </div>
            </div>
          ))}
          
          <div className="add-stakeholder">
            <button
              type="button"
              className="btn btn-outline"
              onClick={handleAddStakeholder}
            >
              <Plus size={16} />
              Add Stakeholder
            </button>
          </div>
        </div>
      </div>
      
      <div className="section-header">
        <h3 className="section-title">Resource Management</h3>
        <p className="section-description">Detailed resource information with rates, skills, and availability</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Project Resources</label>
        <div className="resources-container">
          {formData.resources.map((resource, index) => (
            <div key={index} className="resource-item">
              <div className="form-grid-4">
                <div className="form-group">
                  <label className="form-label">Resource Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={resource.name}
                    onChange={(e) => handleResourceChange(index, 'name', e.target.value)}
                    placeholder="e.g., John Smith"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Resource Type</label>
                  <select
                    className="form-select"
                    value={resource.type}
                    onChange={(e) => handleResourceChange(index, 'type', e.target.value)}
                  >
                    <option value="">Select type</option>
                    <option value="employee">Employee</option>
                    <option value="contractor">Contractor</option>
                    <option value="consultant">Consultant</option>
                    <option value="vendor">Vendor</option>
                    <option value="freelancer">Freelancer</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <input
                    type="text"
                    className="form-control"
                    value={resource.role}
                    onChange={(e) => handleResourceChange(index, 'role', e.target.value)}
                    placeholder="e.g., Senior Developer"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Rate</label>
                  <div className="budget-input-group">
                    <input
                      type="number"
                      className="form-control budget-amount"
                      value={resource.rate}
                      onChange={(e) => handleResourceChange(index, 'rate', e.target.value)}
                      placeholder="0"
                    />
                    <select
                      className="form-select budget-currency"
                      value={resource.currency}
                      onChange={(e) => handleResourceChange(index, 'currency', e.target.value)}
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="INR">INR</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="form-grid-4">
                <div className="form-group">
                  <label className="form-label">Hours/Week</label>
                  <input
                    type="number"
                    className="form-control"
                    value={resource.hours}
                    onChange={(e) => handleResourceChange(index, 'hours', e.target.value)}
                    placeholder="40"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={resource.startDate}
                    onChange={(e) => handleResourceChange(index, 'startDate', e.target.value)}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={resource.endDate}
                    onChange={(e) => handleResourceChange(index, 'endDate', e.target.value)}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={resource.status}
                    onChange={(e) => handleResourceChange(index, 'status', e.target.value)}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Experience Level</label>
                  <select
                    className="form-select"
                    value={resource.experience}
                    onChange={(e) => handleResourceChange(index, 'experience', e.target.value)}
                  >
                    <option value="">Select experience</option>
                    <option value="junior">Junior (0-2 years)</option>
                    <option value="mid">Mid-level (3-5 years)</option>
                    <option value="senior">Senior (6-10 years)</option>
                    <option value="expert">Expert (10+ years)</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Location</label>
                  <input
                    type="text"
                    className="form-control"
                    value={resource.location}
                    onChange={(e) => handleResourceChange(index, 'location', e.target.value)}
                    placeholder="e.g., New York, Remote"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Contact</label>
                  <input
                    type="text"
                    className="form-control"
                    value={resource.contact}
                    onChange={(e) => handleResourceChange(index, 'contact', e.target.value)}
                    placeholder="email@example.com"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <input
                    type="text"
                    className="form-control"
                    value={resource.notes}
                    onChange={(e) => handleResourceChange(index, 'notes', e.target.value)}
                    placeholder="Additional notes"
                  />
                </div>
              </div>
              
              <div className="resource-actions">
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleRemoveResource(index)}
                >
                  <X size={16} />
                  Remove Resource
                </button>
              </div>
            </div>
          ))}
          
          <div className="add-resource">
            <button
              type="button"
              className="btn btn-outline"
              onClick={handleAddResource}
            >
              <Plus size={16} />
              Add Resource
            </button>
          </div>
        </div>
      </div>
    </div>
  );
      
  const renderProjectDetails = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Project Details</h3>
        <p className="section-description">Objectives, deliverables, and milestones</p>
      </div>
      <div className="form-group">
        <label className="form-label">Project Objectives</label>
        <div className="array-input-group">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Add objective"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleArrayAdd('objectives', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline"
              onClick={(e) => {
                const input = e.target.parentElement.querySelector('input');
                if (input && input.value.trim()) {
                  handleArrayAdd('objectives', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="array-items">
            {formData.objectives.map((objective, index) => (
              <div key={index} className="array-item">
                <span>{objective}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleArrayRemove('objectives', index)}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Key Deliverables</label>
        <div className="array-input-group">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Add deliverable"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleArrayAdd('deliverables', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline"
              onClick={(e) => {
                const input = e.target.parentElement.querySelector('input');
                if (input && input.value.trim()) {
                  handleArrayAdd('deliverables', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="array-items">
            {formData.deliverables.map((deliverable, index) => (
              <div key={index} className="array-item">
                <span>{deliverable}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleArrayRemove('deliverables', index)}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Milestones</label>
        <div className="array-input-group">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Add milestone"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleArrayAdd('milestones', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline"
              onClick={(e) => {
                const input = e.target.parentElement.querySelector('input');
                if (input && input.value.trim()) {
                  handleArrayAdd('milestones', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="array-items">
            {formData.milestones.map((milestone, index) => (
              <div key={index} className="array-item">
                <span>{typeof milestone === 'string' ? milestone : milestone.name || 'Unnamed Milestone'}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleArrayRemove('milestones', index)}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderCommunication = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Communication</h3>
        <p className="section-description">Reporting, meetings, and collaboration</p>
      </div>
        <div className="form-group">
          <label className="form-label">Reporting Frequency</label>
          <select
            className="form-select"
            value={formData.reportingFrequency}
            onChange={(e) => handleInputChange('reportingFrequency', e.target.value)}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          <option value="bi-weekly">Bi-weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        
        <div className="form-group">
        <label className="form-label">Communication Channels</label>
        <div className="checkbox-group">
          <div className="form-check">
          <input
              type="checkbox"
              className="form-check-input"
              id="email"
              checked={formData.communicationChannels.includes('email')}
              onChange={(e) => {
                const channels = formData.communicationChannels;
                if (e.target.checked) {
                  handleInputChange('communicationChannels', [...channels, 'email']);
                } else {
                  handleInputChange('communicationChannels', channels.filter(c => c !== 'email'));
                }
              }}
            />
            <label className="form-check-label" htmlFor="email">Email</label>
        </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="slack"
              checked={formData.communicationChannels.includes('slack')}
              onChange={(e) => {
                const channels = formData.communicationChannels;
                if (e.target.checked) {
                  handleInputChange('communicationChannels', [...channels, 'slack']);
                } else {
                  handleInputChange('communicationChannels', channels.filter(c => c !== 'slack'));
                }
              }}
            />
            <label className="form-check-label" htmlFor="slack">Slack</label>
      </div>
          <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
              id="teams"
              checked={formData.communicationChannels.includes('teams')}
                onChange={(e) => {
                const channels = formData.communicationChannels;
                if (e.target.checked) {
                  handleInputChange('communicationChannels', [...channels, 'teams']);
                } else {
                  handleInputChange('communicationChannels', channels.filter(c => c !== 'teams'));
                }
              }}
            />
            <label className="form-check-label" htmlFor="teams">Microsoft Teams</label>
            </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Meeting Schedule</label>
        <input
          type="text"
          className="form-control"
          value={formData.meetingSchedule}
          onChange={(e) => handleInputChange('meetingSchedule', e.target.value)}
          placeholder="e.g., Every Monday at 10:00 AM"
        />
      </div>
    </div>
  );
      
  const renderIntegration = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Integration & Tools</h3>
        <p className="section-description">Project tools and system integrations</p>
      </div>
        <div className="form-group">
        <label className="form-label">Project Tools</label>
          <div className="checkbox-group">
          <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
              id="jira"
              checked={formData.projectTools.includes('jira')}
                  onChange={(e) => {
                const tools = formData.projectTools;
                if (e.target.checked) {
                  handleInputChange('projectTools', [...tools, 'jira']);
                } else {
                  handleInputChange('projectTools', tools.filter(t => t !== 'jira'));
                }
              }}
            />
            <label className="form-check-label" htmlFor="jira">Jira</label>
              </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="asana"
              checked={formData.projectTools.includes('asana')}
              onChange={(e) => {
                const tools = formData.projectTools;
                if (e.target.checked) {
                  handleInputChange('projectTools', [...tools, 'asana']);
                } else {
                  handleInputChange('projectTools', tools.filter(t => t !== 'asana'));
                }
              }}
            />
            <label className="form-check-label" htmlFor="asana">Asana</label>
          </div>
          <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
              id="trello"
              checked={formData.projectTools.includes('trello')}
                  onChange={(e) => {
                const tools = formData.projectTools;
                if (e.target.checked) {
                  handleInputChange('projectTools', [...tools, 'trello']);
                } else {
                  handleInputChange('projectTools', tools.filter(t => t !== 'trello'));
                }
              }}
            />
            <label className="form-check-label" htmlFor="trello">Trello</label>
          </div>
        </div>
      </div>
      
        <div className="form-group">
          <label className="form-label">Document Repository</label>
          <input
            type="text"
            className="form-control"
            value={formData.documentRepository}
            onChange={(e) => handleInputChange('documentRepository', e.target.value)}
          placeholder="e.g., SharePoint, Google Drive, Confluence"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Knowledge Base</label>
          <input
            type="text"
            className="form-control"
            value={formData.knowledgeBase}
            onChange={(e) => handleInputChange('knowledgeBase', e.target.value)}
          placeholder="e.g., Confluence, Notion, Wiki"
          />
      </div>
    </div>
  );

  const renderCompliance = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Compliance & Security</h3>
        <p className="section-description">Security, compliance, and approval workflows</p>
      </div>
        <div className="form-group">
          <label className="form-label">Security Level</label>
          <select
            className="form-select"
            value={formData.securityLevel}
            onChange={(e) => handleInputChange('securityLevel', e.target.value)}
          >
          <option value="standard">Standard</option>
          <option value="high">High</option>
            <option value="confidential">Confidential</option>
            <option value="restricted">Restricted</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Data Classification</label>
          <select
            className="form-select"
            value={formData.dataClassification}
            onChange={(e) => handleInputChange('dataClassification', e.target.value)}
          >
            <option value="public">Public</option>
            <option value="internal">Internal</option>
            <option value="confidential">Confidential</option>
          <option value="restricted">Restricted</option>
          </select>
      </div>
      
      <div className="form-group">
        <label className="form-label">Approval Workflow</label>
        <select
          className="form-select"
          value={formData.approvalWorkflow}
          onChange={(e) => handleInputChange('approvalWorkflow', e.target.value)}
        >
          <option value="">Select workflow</option>
          <option value="standard">Standard Approval</option>
          <option value="expedited">Expedited Approval</option>
          <option value="executive">Executive Approval</option>
          <option value="board">Board Approval</option>
        </select>
      </div>
    </div>
  );
      
  const renderFinanceBudgeting = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Budget Breakdown</h3>
        <p className="section-description">Detailed budget allocation by category</p>
      </div>
      
      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">Labor Costs</label>
          <div className="budget-input-group">
              <input
              type="number"
              className="form-control budget-amount"
              value={formData.budgetBreakdown?.labor || 0}
              onChange={(e) => handleInputChange('budgetBreakdown', {...formData.budgetBreakdown, labor: e.target.value})}
              placeholder="0"
            />
            <select className="form-select budget-currency">
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="INR">INR</option>
            </select>
            </div>
        </div>
        
        <div className="form-group">
          <label className="form-label">Materials</label>
          <div className="budget-input-group">
            <input
              type="number"
              className="form-control budget-amount"
              value={formData.budgetBreakdown?.materials || 0}
              onChange={(e) => handleInputChange('budgetBreakdown', {...formData.budgetBreakdown, materials: e.target.value})}
              placeholder="0"
            />
            <select className="form-select budget-currency">
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="INR">INR</option>
            </select>
          </div>
        </div>
        
        <div className="form-group">
          <label className="form-label">Equipment</label>
          <div className="budget-input-group">
              <input
              type="number"
              className="form-control budget-amount"
              value={formData.budgetBreakdown?.equipment || 0}
              onChange={(e) => handleInputChange('budgetBreakdown', {...formData.budgetBreakdown, equipment: e.target.value})}
              placeholder="0"
            />
            <select className="form-select budget-currency">
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="INR">INR</option>
            </select>
            </div>
        </div>
        
        <div className="form-group">
          <label className="form-label">Travel & Expenses</label>
          <div className="budget-input-group">
            <input
              type="number"
              className="form-control budget-amount"
              value={formData.budgetBreakdown?.travel || 0}
              onChange={(e) => handleInputChange('budgetBreakdown', {...formData.budgetBreakdown, travel: e.target.value})}
              placeholder="0"
            />
            <select className="form-select budget-currency">
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="INR">INR</option>
            </select>
          </div>
        </div>
      </div>
      
      <div className="section-header">
        <h3 className="section-title">Financial Controls</h3>
        <p className="section-description">Budget approval and financial reporting settings</p>
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Budget Approval Required</label>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
              id="budgetApprovalRequired"
              checked={formData.budgetApprovalRequired}
              onChange={(e) => handleInputChange('budgetApprovalRequired', e.target.checked)}
              />
            <label className="form-check-label" htmlFor="budgetApprovalRequired">Require budget approval</label>
            </div>
          </div>
        
        <div className="form-group">
          <label className="form-label">Budget Approver</label>
          <select
            className="form-select"
            value={formData.budgetApprover}
            onChange={(e) => handleInputChange('budgetApprover', e.target.value)}
          >
            <option value="">Select approver</option>
            <option value="finance-manager">Finance Manager</option>
            <option value="director">Director</option>
            <option value="ceo">CEO</option>
          </select>
        </div>
        </div>
        
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">ROI Target (%)</label>
              <input
            type="number"
            className="form-control"
            value={formData.roiTarget}
            onChange={(e) => handleInputChange('roiTarget', e.target.value)}
            placeholder="e.g., 15"
          />
            </div>
        
        <div className="form-group">
          <label className="form-label">Budget Variance Threshold (%)</label>
          <input
            type="number"
            className="form-control"
            value={formData.budgetVarianceThreshold}
            onChange={(e) => handleInputChange('budgetVarianceThreshold', e.target.value)}
            placeholder="e.g., 10"
          />
          </div>
        </div>
      
      <div className="form-group">
        <label className="form-label">Financial Reporting Frequency</label>
        <select
          className="form-select"
          value={formData.financialReporting}
          onChange={(e) => handleInputChange('financialReporting', e.target.value)}
        >
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
          <option value="as-needed">As Needed</option>
        </select>
      </div>
      
      <div className="section-header">
        <h3 className="section-title">Billing Milestones</h3>
        <p className="section-description">Set up invoice triggers, amounts, and billing dates</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Billing Milestones</label>
        <div className="billing-milestones-container">
          {formData.billingMilestones.map((milestone, index) => (
            <div key={index} className="billing-milestone-item">
              <div className="form-grid-4">
                <div className="form-group">
                  <label className="form-label">Milestone Name</label>
            <input
              type="text"
              className="form-control"
                    value={milestone.name}
                    onChange={(e) => handleBillingMilestoneChange(index, 'name', e.target.value)}
                    placeholder="e.g., Project Kickoff"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Billing Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={milestone.billingDate}
                    onChange={(e) => handleBillingMilestoneChange(index, 'billingDate', e.target.value)}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Amount</label>
                  <div className="budget-input-group">
                    <input
                      type="number"
                      className="form-control budget-amount"
                      value={milestone.amount}
                      onChange={(e) => handleBillingMilestoneChange(index, 'amount', e.target.value)}
                      placeholder="0"
                    />
                    <select 
                      className="form-select budget-currency"
                      value={milestone.currency}
                      onChange={(e) => handleBillingMilestoneChange(index, 'currency', e.target.value)}
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="INR">INR</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Trigger Condition</label>
                  <select
                    className="form-select"
                    value={milestone.trigger}
                    onChange={(e) => handleBillingMilestoneChange(index, 'trigger', e.target.value)}
                  >
                    <option value="date">On Date</option>
                    <option value="milestone">On Milestone Completion</option>
                    <option value="percentage">On Project % Complete</option>
                    <option value="deliverable">On Deliverable</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input
                    type="text"
                    className="form-control"
                    value={milestone.description}
                    onChange={(e) => handleBillingMilestoneChange(index, 'description', e.target.value)}
                    placeholder="Billing milestone description"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Auto-Generate Invoice</label>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`autoInvoice-${index}`}
                      checked={milestone.autoGenerate}
                      onChange={(e) => handleBillingMilestoneChange(index, 'autoGenerate', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor={`autoInvoice-${index}`}>
                      Auto-generate invoice
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="billing-milestone-actions">
            <button
              type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleRemoveBillingMilestone(index)}
                >
                  <X size={16} />
                  Remove Milestone
            </button>
          </div>
            </div>
          ))}
          
          <div className="add-billing-milestone">
                <button
                  type="button"
              className="btn btn-outline"
              onClick={handleAddBillingMilestone}
                >
              <Plus size={16} />
              Add Billing Milestone
                </button>
              </div>
        </div>
      </div>
    </div>
  );

  const renderAdvancedSettings = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Advanced Settings</h3>
        <p className="section-description">Notifications, tracking, and custom fields</p>
      </div>
      <div className="form-group">
        <label className="form-label">Notifications & Alerts</label>
        <div className="checkbox-group">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="autoNotifications"
              checked={formData.autoNotifications}
              onChange={(e) => handleInputChange('autoNotifications', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="autoNotifications">Auto Notifications</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="timeTracking"
              checked={formData.timeTracking}
              onChange={(e) => handleInputChange('timeTracking', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="timeTracking">Time Tracking</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="budgetAlerts"
              checked={formData.budgetAlerts}
              onChange={(e) => handleInputChange('budgetAlerts', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="budgetAlerts">Budget Alerts</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="riskMonitoring"
              checked={formData.riskMonitoring}
              onChange={(e) => handleInputChange('riskMonitoring', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="riskMonitoring">Risk Monitoring</label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <div>
            {renderBasicInformation()}
            {renderTimelineBudget()}
          </div>
        );
      case 'team':
        return (
          <div>
            {renderTeamResources()}
            {renderProjectDetails()}
          </div>
        );
      case 'finance':
        return renderFinanceBudgeting();
      case 'communication':
        return (
          <div>
            {renderCommunication()}
            {renderIntegration()}
          </div>
        );
      case 'advanced':
        return (
          <div>
            {renderCompliance()}
            {renderAdvancedSettings()}
          </div>
        );
      default:
        return renderBasicInformation();
    }
  };

  if (isLoading) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader 
          title="Edit Project"
          description="Update project information and settings"
        />
        <div className="page-container">
          <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Edit Project"
        description="Update project information and settings"
      />
      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        {/* Main Content */}
        <div className="project-create-container" style={{ width: '100%', maxWidth: '100%' }}>
        <form onSubmit={handleSubmit}>
          {/* Tab Navigation */}
          <div className="project-tabs">
            <div className="project-tabs-nav">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <div className="project-tab-icon">
                    {tab.icon}
                  </div>
                  <div className="project-tab-content">
                    <div className="project-tab-label">{tab.label}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="project-tab-content">
            <div className="project-form-card">
              {renderTabContent()}
            </div>
          </div>

          {/* Form Actions */}
          <div className="project-form-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate('/projects/project-management')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Updating Project...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Update Project
                </>
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default EditProject;